import React, { useCallback, useEffect, useState } from "react"
import {
  TextFormatType,
  FORMAT_TEXT_COMMAND,
  CommandPayloadType,
  LexicalCommand,
  $getSelection,
  HISTORIC_TAG,
  $isRangeSelection,
  $isElementNode,
  $isRootOrShadowRoot,
  $isNodeSelection,
  SELECTION_CHANGE_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  KEY_ENTER_COMMAND,
  LexicalNode,
  COMMAND_PRIORITY_HIGH,
  $addUpdateTag,
  SKIP_DOM_SELECTION_TAG,
} from "lexical"
import { $isHeadingNode } from "@lexical/rich-text"
import { $patchStyleText, $getSelectionStyleValueForProperty } from "@lexical/selection"
import { $isParentElementRTL } from "@lexical/selection"
import { $findMatchingParent, $getNearestNodeOfType, $isEditorIsNestedEditor } from "@lexical/utils"
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link"
import { $isListNode, ListNode } from "@lexical/list"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import {
  formatBulletList,
  formatCheckList,
  formatOrderedList,
  getSelectedNode,
  sanitizeUrl,
} from "../../utils"
import DropdownColorPicker from "../DropdownColorPicker/DropdownColorPicker"
import { useToolbarState } from "./ToolbarContext"
import { $isTableNode, $isTableSelection } from "@lexical/table"
import { INSERT_IMAGE_COMMAND } from "../ImagePlugin/ImagePlugin"
import useAction from "../../../../../hooks/useAction"
import actions from "../../../../../store/actions"
import { InsertTableDialog } from "../TablePlugin/TablePlugin"
import useModal from "../TablePlugin/useModal"
import { useIntl } from "react-intl"
import fontBoldIcon from "../../icons/font-bold.svg"
import fontItalicIcon from "../../icons/font-italic.svg"
import fontUnderlineIcon from "../../icons/font-underline.svg"
import linkIcon from "../../icons/link.svg"
import orderedListIcon from "../../icons/ordered-list.svg"
import unorderedListIcon from "../../icons/unordered-list.svg"
import checkListIcon from "../../icons/check-list.svg"
import imageIcon from "../../icons/image.svg"
import tableIcon from "../../icons/table.svg"
import FontSizePlugin, { parseFontSizeForToolbar } from "../FontSizePlugin/FontSizePlugin"
import classNames from "classnames"

export const blockTypeToBlockName = {
  bullet: "Bulleted List",
  check: "Check List",
  code: "Code Block",
  h1: "Heading 1",
  h2: "Heading 2",
  h3: "Heading 3",
  h4: "Heading 4",
  h5: "Heading 5",
  h6: "Heading 6",
  number: "Numbered List",
  paragraph: "Normal",
  quote: "Quote",
}

const EditorToolbar = ({ setIsLinkEditMode, disabled, setIsColorPickerOpen, className }: any) => {
  const [editor] = useLexicalComposerContext()

  const [activeEditor, setActiveEditor] = useState(editor)
  const { toolbarState, updateToolbarState } = useToolbarState()
  const [modal, showModal] = useModal()
  const intl = useIntl()
  const uploadFile = useAction(actions.documentsActions.uploadRteFile)

  function $findTopLevelElement(node: any) {
    let topLevelElement =
      node.getKey() === "root"
        ? node
        : $findMatchingParent(node, (e) => {
            const parent = e.getParent()
            return parent !== null && $isRootOrShadowRoot(parent)
          })

    if (topLevelElement === null) {
      topLevelElement = node.getTopLevelElementOrThrow()
    }
    return topLevelElement
  }

  const $handleHeadingNode = useCallback(
    (selectedElement: LexicalNode) => {
      const type = $isHeadingNode(selectedElement)
        ? selectedElement.getTag()
        : selectedElement.getType()

      if (type in blockTypeToBlockName) {
        updateToolbarState("blockType", type as any)
      }
    },
    [updateToolbarState]
  )

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      if (activeEditor !== editor && $isEditorIsNestedEditor(activeEditor)) {
        const rootElement = activeEditor.getRootElement()
        updateToolbarState(
          "isImageCaption",
          !!rootElement?.parentElement?.classList.contains("image-caption-container")
        )
      } else {
        updateToolbarState("isImageCaption", false)
      }

      const anchorNode = selection.anchor.getNode()
      const element = $findTopLevelElement(anchorNode)
      const elementKey = element.getKey()
      const elementDOM = activeEditor.getElementByKey(elementKey)

      updateToolbarState("isRTL", $isParentElementRTL(selection))

      // Update links
      const node = getSelectedNode(selection)
      const parent = node.getParent()
      const isLink = $isLinkNode(parent) || $isLinkNode(node)
      updateToolbarState("isLink", isLink)

      const tableNode = $findMatchingParent(node, $isTableNode)
      if ($isTableNode(tableNode)) {
        updateToolbarState("rootType", "table")
      } else {
        updateToolbarState("rootType", "root")
      }

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode)
          const type = parentList ? parentList.getListType() : element.getListType()

          updateToolbarState("blockType", type)
        } else {
          $handleHeadingNode(element)
        }
      }

      // Handle buttons
      updateToolbarState(
        "fontColor",
        $getSelectionStyleValueForProperty(selection, "color", "#000")
      )
      updateToolbarState(
        "bgColor",
        $getSelectionStyleValueForProperty(selection, "background-color", "#fff")
      )
      updateToolbarState(
        "fontFamily",
        $getSelectionStyleValueForProperty(selection, "font-family", "Arial")
      )
      let matchingParent
      if ($isLinkNode(parent)) {
        // If node is a link, we need to fetch the parent paragraph node to set format
        matchingParent = $findMatchingParent(
          node,
          (parentNode) => $isElementNode(parentNode) && !parentNode.isInline()
        )
      }

      // If matchingParent is a valid node, pass its format type
      updateToolbarState(
        "elementFormat",
        $isElementNode(matchingParent)
          ? matchingParent.getFormatType()
          : $isElementNode(node)
            ? node.getFormatType()
            : parent?.getFormatType() || "left"
      )
    }
    if ($isRangeSelection(selection) || $isTableSelection(selection)) {
      // Update text format
      updateToolbarState("isBold", selection.hasFormat("bold"))
      updateToolbarState("isItalic", selection.hasFormat("italic"))
      updateToolbarState("isUnderline", selection.hasFormat("underline"))
      updateToolbarState("isStrikethrough", selection.hasFormat("strikethrough"))
      updateToolbarState("isSubscript", selection.hasFormat("subscript"))
      updateToolbarState("isSuperscript", selection.hasFormat("superscript"))
      updateToolbarState("isHighlight", selection.hasFormat("highlight"))
      updateToolbarState("isCode", selection.hasFormat("code"))
      updateToolbarState(
        "fontSize",
        $getSelectionStyleValueForProperty(selection, "font-size", "15px")
      )
      updateToolbarState("isLowercase", selection.hasFormat("lowercase"))
      updateToolbarState("isUppercase", selection.hasFormat("uppercase"))
      updateToolbarState("isCapitalize", selection.hasFormat("capitalize"))
    }
    if ($isNodeSelection(selection)) {
      const nodes = selection.getNodes()
      for (const selectedNode of nodes) {
        const parentList = $getNearestNodeOfType<ListNode>(selectedNode, ListNode)
        if (parentList) {
          const type = parentList.getListType()
          updateToolbarState("blockType", type)
        } else {
          const selectedElement = $findTopLevelElement(selectedNode)
          $handleHeadingNode(selectedElement)
          // Update elementFormat for node selection (e.g., images)
          if ($isElementNode(selectedElement)) {
            updateToolbarState("elementFormat", selectedElement.getFormatType())
          }
        }
      }
    }
  }, [activeEditor, editor, updateToolbarState, $handleHeadingNode])

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        setActiveEditor(newEditor)
        $updateToolbar()
        return false
      },
      COMMAND_PRIORITY_CRITICAL
    )
  }, [editor, $updateToolbar, setActiveEditor])

  useEffect(() => {
    editor.registerCommand(
      KEY_ENTER_COMMAND,
      () => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection)) {
          return false
        }

        // reset all text formatting and styles on new line
        editor.update(() => {
          selection.setFormat(0)
          selection.setStyle("")
        })

        return true // Indicate that the command was handled
      },
      COMMAND_PRIORITY_HIGH
    )
  }, [editor])

  const insertLink = useCallback(() => {
    if (!toolbarState.isLink) {
      setIsLinkEditMode(true)
      activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl("https://"))
    } else {
      setIsLinkEditMode(false)
      activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
    }
  }, [activeEditor, toolbarState.isLink])

  const applyStyleText = useCallback(
    (styles: Record<string, string>, skipHistoryStack?: boolean, skipRefocus: boolean = false) => {
      activeEditor.update(
        () => {
          if (skipRefocus) {
            $addUpdateTag(SKIP_DOM_SELECTION_TAG)
          }
          const selection = $getSelection()
          if (selection !== null) {
            $patchStyleText(selection, styles)
          }
        },
        skipHistoryStack ? { tag: HISTORIC_TAG } : {}
      )
    },
    [activeEditor]
  )

  const onFontColorSelect = useCallback(
    (value: string, skipHistoryStack: boolean) => {
      applyStyleText({ color: value }, skipHistoryStack)
    },
    [applyStyleText]
  )

  const onBgColorSelect = useCallback(
    (value: string, skipHistoryStack: boolean, skipRefocus: boolean) => {
      applyStyleText({ "background-color": value }, skipHistoryStack, skipRefocus)
    },
    [applyStyleText]
  )

  const dispatchToolbarCommand = <T extends LexicalCommand<unknown>>(
    command: T,
    payload: CommandPayloadType<T> | undefined = undefined,
    skipRefocus: boolean = false
  ) => {
    activeEditor.update(() => {
      if (skipRefocus) {
        $addUpdateTag(SKIP_DOM_SELECTION_TAG)
      }

      // Re-assert on Type so that payload can have a default param
      activeEditor.dispatchCommand(command, payload as CommandPayloadType<T>)
    })
  }

  const dispatchFormatTextCommand = (payload: TextFormatType, skipRefocus: boolean = false) =>
    dispatchToolbarCommand(FORMAT_TEXT_COMMAND, payload, skipRefocus)

  const addImage = (e: FileList) => {
    if (e?.[0]) {
      uploadFile({ file: e?.[0] }).then((result: { media: { url: string; name: string } }) => {
        editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
          altText: result.media.name,
          src: result.media.url,
        })
      })
    }
  }

  return (
    <>
      <div className={classNames("LexicalEditor__toolbar", className)}>
        <button
          className="toolbar-item"
          disabled={disabled}
          onClick={() => dispatchFormatTextCommand("bold")}
          type="button"
        >
          <img src={fontBoldIcon} alt="Bold" />
        </button>
        <button
          className="toolbar-item"
          disabled={disabled}
          onClick={() => dispatchFormatTextCommand("italic")}
          type="button"
        >
          <img src={fontItalicIcon} alt="Italic" />
        </button>
        <button
          className="toolbar-item"
          disabled={disabled}
          onClick={() => dispatchFormatTextCommand("underline")}
          type="button"
        >
          <img src={fontUnderlineIcon} alt="Underline" />
        </button>
        <FontSizePlugin
          selectionFontSize={parseFontSizeForToolbar(toolbarState.fontSize).slice(0, -2)}
          editor={activeEditor}
          disabled={disabled}
        />
        <DropdownColorPicker
          disabled={disabled}
          buttonClassName="toolbar-item color-picker"
          buttonAriaLabel="Formatting text color"
          buttonIconClassName="icon font-color"
          color={toolbarState.fontColor}
          onChange={onFontColorSelect}
          title="text color"
          setIsColorPickerOpen={setIsColorPickerOpen}
        />
        <DropdownColorPicker
          disabled={disabled}
          buttonClassName="toolbar-item color-picker"
          buttonAriaLabel="Formatting background color"
          buttonIconClassName="icon bg-color"
          color={toolbarState.bgColor}
          onChange={onBgColorSelect}
          title="bg color"
          setIsColorPickerOpen={setIsColorPickerOpen}
        />
        <button
          disabled={disabled}
          onClick={insertLink}
          className={"toolbar-item spaced " + (toolbarState.isLink ? "active" : "")}
          aria-label="Insert link"
          type="button"
        >
          <img src={linkIcon} alt="Link" />
        </button>
        <>
          <button
            className="toolbar-item"
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault()
              formatOrderedList(activeEditor, toolbarState.blockType)
            }}
          >
            <img src={orderedListIcon} alt="Ordered list" />
          </button>
          <button
            className="toolbar-item"
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault()
              formatBulletList(activeEditor, toolbarState.blockType)
            }}
          >
            <img src={unorderedListIcon} alt="Unordered list" />
          </button>
          <button
            className="toolbar-item"
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault()
              formatCheckList(activeEditor, toolbarState.blockType)
            }}
          >
            <img src={checkListIcon} alt="Check list" />
          </button>
          <label className="toolbar-item">
            <img src={imageIcon} alt="Image" />
            <input
              disabled={disabled}
              style={{ display: "none" }}
              type="file"
              onChange={(e) => {
                if (e.target.files) {
                  addImage(e.target.files)
                }
              }}
            />
          </label>
          <button
            className="toolbar-item"
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault()
              showModal(intl.formatMessage({ id: "common.insertTable" }), (onClose: () => void) => (
                <InsertTableDialog activeEditor={activeEditor} onClose={onClose} />
              ))
            }}
          >
            <img src={tableIcon} alt="Table Icon" />
          </button>
        </>
      </div>
      {modal}
    </>
  )
}

export default EditorToolbar
